import { toAsciiRows } from '../content/asciiMap';
import { BIOMES, biomeForLevel, type BiomeDef } from '../content/biomes';
import {
  allowedMowers,
  loadLevel,
  type LevelDef,
  type LevelFile,
  type MowerRule,
} from '../content/levelSchema';
import { MOWER_DEFS, MOWERS, type MowerId } from '../content/mowers';
import { BIG_TREE_CROWN, TILE_BY_ID, TILES, type TileId } from '../content/tiles';
import type { CritterKind, CritterSpawn } from '../sim/critters';
import { Dir, DIRECTIONS, dx, dy, type Direction } from '../sim/direction';
import { isSolid, Tile } from '../sim/grid';
import type { BallSpec } from '../sim/balls';
import { fallTiles, type MoleSpec, type PickupKind, type PickupSpawn } from '../sim/level';
import { Rng } from '../sim/rng';
import { Simulation } from '../sim/simulation';
import { solveGreedy } from './solver/greedyPlanner';

/**
 * Bump when generation output changes for the same seed. Baked campaign levels record it, so they
 * stay stable (they're stored as data) while the generator keeps evolving.
 */
export const GENERATOR_VERSION = 3;

export interface GenParams {
  /** Difficulty level (the campaign number, or an equivalent for daily/endless). */
  level: number;
  seed: string;
  id: string;
  biome: BiomeDef;
  width: number;
  height: number;
  /** Difficulty points to spend on terrain patches and scattered obstacles. */
  budget: number;
  terrainPatches: number;
  critters: number;
  coins: number;
  rare: boolean;
  repair: boolean;
  mowerRule: MowerRule;
  /**
   * Overgrown lawn: this share (0..1) of the plain grass is tall grass, leaving a few patches of
   * ordinary grass. Tall grass takes extra passes, gas and time (docs/GDD.md, Terrain).
   */
  overgrown?: number;
  /** Garden hoses snaking in from the lawn edge (chewing one costs coins). */
  hoses?: number;
  /** A dog house, with a dog that trots out now and then. */
  dog?: boolean;
  /** Sprinklers: heads to keep off, spraying slippery tiles on a timer. */
  sprinklers?: number;
  /** Moles pushing up molehills through mowed lawn, to be flattened. */
  moles?: MoleSpec;
  /** Big trees: a trunk with a wide crown that hides the lawn beneath it. */
  bigTrees?: number;
  /** Thorn patches (flat tires): placed on purpose, never needed to finish the lawn. */
  thorns?: number;
  /** Dead trees that creak and fall during play, leaving a log across the lawn. */
  falls?: number;
  /** Old lanterns spread over the lawn, one to a region (Lights Out). */
  lanterns?: number;
  /** Mirror the layout left to right (the Grand Estate's formal gardens). */
  symmetric?: boolean;
  /** A river across the lawn, crossed by bridges. */
  river?: boolean;
  /** A terrace with planted urns along the top of the lawn. */
  patio?: boolean;
  /** Sloping patches of lawn (uphill slow, downhill quick). */
  hills?: number;
  /** Stray balls rolling across now and then (the Stadium). */
  balls?: BallSpec;
  /** A meandering creek across the lawn, a tile wide, crossed by footbridges (the Meadows). */
  creek?: boolean;
  /** Big rolling hills (up to 7 tiles across) rather than small mounds (the Meadows). */
  bigHills?: boolean;
  /**
   * Campaign lawns (lm-66f): terrain and lawn items laid out as deliberate motifs (mirrored
   * halves, beds and fields, borders, rows, pairs, clusters) instead of scattered one by one.
   * Endless and the Daily keep the random scatter.
   */
  composed?: boolean;
  /** Woodland wildlife (deer, foxes, a stag) instead of garden critters (the Meadows). */
  wildlife?: boolean;
  /**
   * Mowers the lawn must be beatable with. Defaults to every allowed narrow mower; other allowed
   * mowers (wide ones) are kept only if they also manage, and excluded otherwise.
   */
  required?: readonly MowerId[];
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/**
 * The introduction schedule (docs/GDD.md §7): at most one new thing per lawn, about one every 2-3
 * lawns, each practiced before the next. The Backyard teaches the basics, the Park brings lawn
 * hazards (hoses, rabbits, the dog, sprinklers), the Farm its crops, the Golf Course its putting
 * greens (keep off) and moles. Hoses and the dog stay home: none on the golf course. Unlock levels of tiles, mowers
 * and tools (content/) follow the same plan; campaign.test.ts checks every baked lawn against it.
 */
/** Squirrels from here (critters at all). */
export const CRITTERS_FROM = 7;
/** Rabbits join the squirrels from here (the Park). */
export const RABBITS_FROM = 24;

/**
 * Overgrown campaign lawns: every fifth lawn from 14 (tall grass arrives at 9; this gives a few
 * lawns to meet it and to afford Sharpened Blades), from 55% tall grass rising to 80%.
 */
export function overgrownShare(level: number): number | undefined {
  if (level < 14 || level % 5 !== 4) return undefined;
  return Math.round((0.55 + 0.25 * Math.min(1, (level - 14) / 40)) * 100) / 100;
}

/**
 * The difficulty curve (docs/GDD.md §7). Starting points for tuning. The Backyard (1-20) ramps
 * from tiny lawns to the full width; later biomes start a little easier than the last one ended
 * (a breather with new things to learn), climb through the biome, and end on a bigger showpiece.
 */
export function paramsForLevel(
  level: number,
  opts: { seed?: string; id?: string } = {},
): GenParams {
  const biome = biomeForLevel(level);
  if (level > 20) {
    const tier = BIOMES.indexOf(biome); // 1 = park, 2 = farm, ...
    const t = level - biome.levels[0]; // 0..19 within the biome
    const finale = level === biome.levels[1];
    const estate = biome.id === 'estate';
    const stadium = biome.id === 'stadium';
    const meadow = biome.id === 'meadow';
    return {
      ...(stadium ? { symmetric: true } : {}),
      ...(meadow
        ? {
            wildlife: true,
            // (An overgrown meadow keeps to gentle mounds: long grass uphill is slow enough.)
            ...(overgrownShare(level) === undefined ? { bigHills: true } : {}),
            bigTrees: t < 8 ? 1 : 2,
            hills: t < 6 ? 1 : 2,
            ...(hasCreek(level) ? { creek: true } : {}),
          }
        : {}),
      ...(estate
        ? {
            symmetric: true,
            bigTrees: 1,
            ...(hasRiver(level) ? { river: true } : {}),
            ...(hasPatio(level) ? { patio: true } : {}),
            ...(hillCount(level) > 0 ? { hills: hillCount(level) } : {}),
          }
        : {}),
      level,
      seed: opts.seed ?? `campaign:${level}`,
      id: opts.id ?? `campaign-${String(level).padStart(3, '0')}`,
      biome,
      // The Estate's lawns grow wider than the screen (a minimap shows the whole).
      // (An overgrown meadow stays 18 across: long grass over a bigger one is too much.)
      width: meadow
        ? t < 5 || overgrownShare(level) !== undefined
          ? 18
          : t < 12
            ? 20
            : 22
        : estate
          ? t < 4
            ? 14
            : t < 10
              ? 16
              : 18
          : stadium
            ? t < 7
              ? 14
              : 16
            : 12,
      height: meadow
        ? overgrownShare(level) !== undefined
          ? 18
          : clamp(18 + Math.floor(t / 4), 18, 22)
        : finale
          ? 20
          : clamp(15 + Math.floor(t / 4), 15, 18),
      budget: 30 + 8 * (tier - 1) + Math.floor(t * 1.6),
      terrainPatches: 3 + Math.floor(t / 5),
      critters: Math.min(5, 1 + tier + Math.floor(t / 7)),
      coins: 6 + Math.floor(t / 4) + (finale ? 4 : 0),
      rare: finale || level % 4 === 0,
      repair: level % 3 === 1,
      mowerRule: { kind: 'minTier', tier: 0 },
      ...overgrownOpt(level),
    };
  }
  const width = clamp(6 + Math.floor((level - 1) / 2), 6, 12);
  return {
    level,
    seed: opts.seed ?? `campaign:${level}`,
    id: opts.id ?? `campaign-${String(level).padStart(3, '0')}`,
    biome,
    width,
    height: clamp(width + Math.floor((level - 1) / 5), 6, 18),
    budget: Math.floor(level * 2.2) + 1,
    terrainPatches: level >= 5 ? 1 + Math.floor((level - 5) / 4) : 0,
    critters: level >= 7 ? 1 + Math.floor((level - 7) / 5) : 0,
    coins: 2 + Math.floor(level / 3),
    rare: level % 4 === 0,
    repair: level >= 10 && level % 3 === 1,
    mowerRule: { kind: 'minTier', tier: 0 },
    ...overgrownOpt(level),
  };
}

function overgrownOpt(level: number): {
  overgrown?: number;
  hoses?: number;
  dog?: boolean;
  sprinklers?: number;
  moles?: MoleSpec;
  thorns?: number;
  falls?: number;
  balls?: BallSpec;
} {
  const share = overgrownShare(level);
  const hoses = hoseCount(level);
  const sprinklers = sprinklerCount(level);
  const moles = moleSpec(level);
  const thorns = thornPatches(level);
  const falls = fallCount(level);
  const balls = ballSpec(level);
  return {
    ...(balls ? { balls } : {}),
    ...(thorns > 0 ? { thorns } : {}),
    ...(falls > 0 ? { falls } : {}),
    ...(share === undefined ? {} : { overgrown: share }),
    ...(hoses > 0 ? { hoses } : {}),
    ...(hasDog(level) ? { dog: true } : {}),
    ...(sprinklers > 0 ? { sprinklers } : {}),
    ...(moles ? { moles } : {}),
  };
}

/** Garden hoses: every fourth lawn from 22 (where they unlock), two from 42, until the Farm ends. */
export function hoseCount(level: number): number {
  if (level < 22 || level > 60 || (level - 22) % 4 !== 0) return 0;
  return level >= 42 ? 2 : 1;
}

/** A dog house on lawns 27, 37, 47, 57 (the dog arrives at 27; it stays home after the Farm). */
export function hasDog(level: number): boolean {
  return level >= 27 && level <= 60 && level % 10 === 7;
}

/** Moles arrive at 63: every other lawn from there, two molehills rising to four. */
export const MOLES_FROM = 63;
export function moleSpec(level: number): MoleSpec | undefined {
  if (level < MOLES_FROM || (level - MOLES_FROM) % 2 !== 0) return undefined;
  return { hills: Math.min(4, 2 + Math.floor((level - MOLES_FROM) / 6)), first: 30, every: 20 };
}

/** Thorn patches arrive at 83: on every lawn from there (two from 92), every other one from 101. */
export const THORNS_FROM = 83;
export function thornPatches(level: number): number {
  if (level < THORNS_FROM || level > 120) return 0;
  if (level > 100) return level % 2 === 1 ? 1 : 0;
  return level >= 92 ? 2 : 1;
}

/**
 * Falling trees arrive at 88: one on every third lawn from there; on the Estate's mirrored lawns,
 * a pair.
 */
export const FALLS_FROM = 88;
export function fallCount(level: number): number {
  if (level < FALLS_FROM || level > 120 || (level - FALLS_FROM) % 3 !== 0) return 0;
  return level > 100 ? 2 : 1;
}

/**
 * The Grand Estate: rivers from 103 (odd lawns), terraces from 105 (even), hills from 107 (every
 * third lawn).
 */
export const RIVERS_FROM = 103;
export const PATIOS_FROM = 105;
export const HILLS_FROM = 107;
const ESTATE_END = 120;
export function hasRiver(level: number): boolean {
  return level >= RIVERS_FROM && level <= ESTATE_END && level % 2 === 1;
}
export function hasPatio(level: number): boolean {
  return level >= PATIOS_FROM && level <= ESTATE_END && level % 2 === 0;
}
export function hillCount(level: number): number {
  if (level < HILLS_FROM || level > ESTATE_END || (level - HILLS_FROM) % 3 !== 0) return 0;
  return level >= 113 ? 2 : 1;
}

/**
 * Stray balls arrive at 125 (the Stadium): on every lawn from there, coming a little more often as
 * the zone goes on.
 */
export const BALLS_FROM = 125;
export function ballSpec(level: number): BallSpec | undefined {
  if (level < BALLS_FROM || level > 139) return undefined;
  return { first: 18, every: Math.max(9, 15 - Math.floor((level - BALLS_FROM) / 3)) };
}

/**
 * Rolling Meadows: a creek on every other lawn from 143, overgrown ones aside (wildflower meadow from 141, beehives
 * from 151 come with their tiles' unlock levels).
 */
export const CREEKS_FROM = 143;
export function hasCreek(level: number): boolean {
  // (Not on an overgrown lawn: three passes through long grass is enough between footbridges.)
  return (
    level >= CREEKS_FROM && level <= 159 && level % 2 === 1 && overgrownShare(level) === undefined
  );
}

/** Sprinklers: every fifth lawn from 31 (where they arrive), two from 46; none in the Meadows. */
export function sprinklerCount(level: number): number {
  if (level < 31 || level > 140 || (level - 31) % 5 !== 0) return 0;
  return level >= 46 ? 2 : 1;
}

export interface GeneratedLevel {
  file: LevelFile;
  def: LevelDef;
  /** Layout attempts consumed (1 = first try). */
  attempts: number;
}

const MAX_ATTEMPTS = 60;

/** Generates a validated, beatable level. Deterministic for the same params. */
export function generateLevel(params: GenParams): GeneratedLevel {
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    // Ease off the budget if a level keeps failing, so generation always converges.
    const easing = Math.floor((attempt - 1) / 15);
    const budget = Math.max(0, params.budget - easing * Math.ceil(params.budget / 4));
    const rng = Rng.fromSeed(`${params.seed}:v${GENERATOR_VERSION}:${attempt}`);
    const result = tryGenerate({ ...params, budget }, rng);
    if (result) return { ...result, attempts: attempt };
  }
  throw new Error(`${params.id}: no beatable layout after ${MAX_ATTEMPTS} attempts`);
}

// ── Layout ──────────────────────────────────────────────────────────────────────────────────

class Layout {
  readonly art: TileId[];
  /** Tiles kept clear of later features (e.g. fence gates). */
  readonly reserved = new Set<number>();
  /** Where falling trees will land (no pickups there: they would be buried). */
  readonly fallZone = new Set<number>();
  /** Difficulty budget the structures used up (rows of headstones). */
  spent = 0;
  constructor(
    readonly w: number,
    readonly h: number,
  ) {
    this.art = new Array<TileId>(w * h).fill('grass');
  }
  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.w && y < this.h;
  }
  get(x: number, y: number): TileId | null {
    return this.inBounds(x, y) ? (this.art[y * this.w + x] as TileId) : null;
  }
  set(x: number, y: number, id: TileId): void {
    if (this.inBounds(x, y)) this.art[y * this.w + x] = id;
  }
  kind(x: number, y: number): Tile {
    const id = this.get(x, y);
    return id === null ? Tile.Obstacle : TILE_BY_ID[id].kind;
  }
  /** Can a mower or critter be on this tile at all (ignoring cut power)? */
  open(x: number, y: number): boolean {
    const k = this.kind(x, y);
    return !isSolid(k) && k !== Tile.Brush;
  }
  openNeighbors(x: number, y: number): number {
    return DIRECTIONS.filter((d) => this.open(x + dx(d), y + dy(d))).length;
  }
  cells(): { x: number; y: number }[] {
    const out: { x: number; y: number }[] = [];
    for (let y = 0; y < this.h; y++) for (let x = 0; x < this.w; x++) out.push({ x, y });
    return out;
  }
}

function weightedPick<T extends string>(
  rng: Rng,
  weights: Partial<Record<T, number>>,
  allow: (id: T) => boolean,
): T | null {
  const entries = (Object.entries(weights) as [T, number][]).filter(
    ([id, w]) => w > 0 && allow(id),
  );
  const total = entries.reduce((sum, [, w]) => sum + w, 0);
  if (total === 0) return null;
  let roll = rng.nextInt(total);
  for (const [id, w] of entries) {
    if (roll < w) return id;
    roll -= w;
  }
  return null;
}

const unlocked = (id: TileId, level: number) => TILE_BY_ID[id].unlockLevel <= level;

function tryGenerate(p: GenParams, rng: Rng): { file: LevelFile; def: LevelDef } | null {
  const L = new Layout(p.width, p.height);

  // Start in a bottom corner, driving along the bottom edge.
  const startLeft = rng.chance(1, 2);
  const start = { x: startLeft ? 0 : p.width - 1, y: p.height - 1 };
  const startDir: Direction = startLeft ? Dir.E : Dir.W;
  const startArea = (x: number, y: number) =>
    (y >= p.height - 1 && Math.abs(x - start.x) <= 3) ||
    (Math.abs(x - start.x) <= 1 && y >= p.height - 2);
  // A mirrored lawn keeps the start's corner and its twin alike.
  const protectedTile = (x: number, y: number) =>
    L.reserved.has(y * p.width + x) ||
    startArea(x, y) ||
    (!!p.symmetric && startArea(p.width - 1 - x, y));

  const topRows = buildStructures(p, L, rng, protectedTile);
  // From the Cemetery on, sprinklers go in early (the rows leave little open lawn later), with
  // their spray kept off the fence where there is room.
  if (p.level > 80) {
    for (let s = 0; s < (p.sprinklers ?? 0); s++) {
      if (p.symmetric && placeSprinkler(L, rng, protectedTile, 2, true)) continue;
      if (!placeSprinkler(L, rng, protectedTile, 2)) placeSprinkler(L, rng, protectedTile);
    }
  }
  for (let t = 0; t < (p.bigTrees ?? 0); t++) {
    plantBigTree(L, rng, protectedTile, topRows, !!p.symmetric);
  }
  if (p.lanterns) spreadLanterns(L, rng, protectedTile, p.lanterns);
  const hills = layHills(p, L, rng, topRows);
  const falls: NonNullable<LevelFile['falls']> = [];
  // (On a mirrored lawn each tree has a twin, falling the mirrored way.)
  for (let t = 0; t < (p.falls ?? 0); t += p.symmetric ? 2 : 1) {
    falls.push(...placeFallingTree(L, rng, protectedTile, start, topRows, !!p.symmetric));
  }
  if (falls.length < (p.falls ?? 0)) return null;
  for (let n = 0; n < (p.thorns ?? 0); n++) {
    thornPatch(L, rng, protectedTile, topRows, start, !!p.symmetric);
  }
  // The schedule's thorns and hills must be there: a layout without room for them is retried.
  if ((p.thorns ?? 0) > 0 && !L.art.includes('thorns')) return null;
  if ((p.hills ?? 0) > 0 && hills.length === 0) return null;
  if (p.river && !L.art.includes('bridge')) return null;

  // Terrain patches get up to ~40% of the budget; obstacles get the rest.
  let budget = Math.max(0, p.budget - L.spent);
  let terrainBudget = Math.floor(budget * 0.4);
  // Campaign lawns (lm-66f): formal and tidy ones are laid out as motifs, and the scatter below
  // then adds only what grows or falls on its own; wild ones are the scatter alone.
  const style: LawnStyle = p.composed ? lawnStyle(p, rng) : 'wild';
  const composed = style !== 'wild';
  if (composed) {
    budget = composeLawn(p, L, rng, protectedTile, topRows, budget, style === 'formal');
    terrainBudget = 0;
  }
  for (let n = 0; n < (composed ? 0 : p.terrainPatches); n++) {
    // (An overgrown meadow is all about its long grass: no regrowing patches on top.)
    const id = weightedPick<TileId>(
      rng,
      p.biome.terrainWeights,
      (t) => unlocked(t, p.level) && !(p.overgrown && t === 'meadowGrass'),
    );
    if (!id) break;
    const cx = rng.nextInt(p.width);
    const cy = rng.intBetween(topRows, p.height - 1);
    const cost = TILE_BY_ID[id].cost;
    for (const [ox, oy] of patchShape(id, rng)) {
      const x = cx + ox;
      const y = cy + oy;
      if (L.get(x, y) !== 'grass' || protectedTile(x, y) || terrainBudget < cost) continue;
      L.set(x, y, id);
      terrainBudget -= cost;
      budget -= cost;
      // Formal gardens: the same bed on the other side.
      const mx = p.width - 1 - x;
      if (p.symmetric && L.get(mx, y) === 'grass' && !protectedTile(mx, y)) {
        L.set(mx, y, id);
        budget -= cost;
      }
    }
  }
  // Scatter obstacles by budget, never orthogonally next to another obstacle (no sealed lanes).
  // After motifs, only the natural filler (rocks, sticks, dead trees, hives), never mirrored.
  const scatterIds = TILES.filter((t) => t.placement === 'scatter').map((t) => t.id);
  const scatterable = (t: TileId) => (composed ? NATURAL_ITEMS.has(t) : scatterIds.includes(t));
  for (let tries = 0; tries < 200 && budget > 0; tries++) {
    const id = weightedPick<TileId>(
      rng,
      p.biome.scatterWeights,
      (t) => scatterable(t) && unlocked(t, p.level) && TILE_BY_ID[t].cost <= budget,
    );
    if (!id) break;
    const x = rng.nextInt(p.width);
    const y = rng.intBetween(topRows, p.height - 1);
    if (L.get(x, y) !== 'grass' || protectedTile(x, y)) continue;
    const touches = DIRECTIONS.some((d) => {
      const k = L.kind(x + dx(d), y + dy(d));
      return (
        L.inBounds(x + dx(d), y + dy(d)) && (isSolid(k) || k === Tile.Brush || k === Tile.Thorns)
      );
    });
    if (touches) continue;
    // From the Cemetery on (narrow lanes between rows), no obstacle may leave a dead end.
    if (p.level > 80 && makesDeadEnd(L, x, y)) continue;
    if (p.symmetric && !composed) {
      // Formal gardens: everything in pairs, mirrored (or on the middle line).
      const mx = p.width - 1 - x;
      if (mx !== x) {
        if (Math.abs(mx - x) <= 1 || L.get(mx, y) !== 'grass' || protectedTile(mx, y)) continue;
        const twinTouches = DIRECTIONS.some((d) => {
          const k = L.kind(mx + dx(d), y + dy(d));
          return (
            L.inBounds(mx + dx(d), y + dy(d)) &&
            (isSolid(k) || k === Tile.Brush || k === Tile.Thorns)
          );
        });
        if (twinTouches || makesDeadEnd(L, mx, y)) continue;
        L.set(mx, y, id);
        budget -= TILE_BY_ID[id].cost;
      }
    }
    L.set(x, y, id);
    budget -= TILE_BY_ID[id].cost;
  }

  for (let h = 0; h < (p.hoses ?? 0); h++) layHose(L, rng, protectedTile);
  if (p.level <= 80) {
    for (let s = 0; s < (p.sprinklers ?? 0); s++) placeSprinkler(L, rng, protectedTile);
  }
  // (Long grass may grow under crowns and where logs will land; only the start stays short.)
  if (p.overgrown)
    overgrow(
      L,
      p.overgrown,
      rng,
      (x, y) => protectedTile(x, y) && !L.reserved.has(y * p.width + x),
    );

  // Critters, in open areas away from the start and from each other. The dog first, at its door.
  const critters: CritterSpawn[] = [];
  if (p.dog) {
    const door = placeDogHouse(L, rng, protectedTile, topRows);
    if (door) critters.push({ kind: 'dog', ...door });
  }
  const critterCells = rng.shuffle(
    L.cells().filter(
      ({ x, y }) =>
        TILE_BY_ID[L.get(x, y) ?? 'rock'].kind === Tile.Grass &&
        Math.abs(x - start.x) + Math.abs(y - start.y) >= 5 &&
        L.openNeighbors(x, y) >= 3 &&
        // Somewhere to run: at least two neighbors that are themselves roomy.
        DIRECTIONS.filter((d) => L.openNeighbors(x + dx(d), y + dy(d)) >= 3).length >= 2,
    ),
  );
  for (const c of critterCells) {
    if (critters.length >= p.critters) break;
    if (critters.some((o) => Math.abs(o.x - c.x) + Math.abs(o.y - c.y) < 4)) continue;
    if (p.wildlife) {
      // Woodland wildlife: deer in pairs, foxes, and now and then a stag (one at most).
      const stag = !critters.some((o) => o.kind === 'stag') && rng.chance(1, 4);
      const kind: CritterKind = stag ? 'stag' : rng.chance(3, 5) ? 'deer' : 'fox';
      critters.push({ kind, ...c });
      if (kind === 'deer' && critters.length < p.critters) {
        const mate = DIRECTIONS.map((d) => ({ x: c.x + dx(d), y: c.y + dy(d) })).find(
          (n) =>
            TILE_BY_ID[L.get(n.x, n.y) ?? 'rock'].kind === Tile.Grass &&
            L.openNeighbors(n.x, n.y) >= 3 &&
            !critters.some((o) => o.x === n.x && o.y === n.y),
        );
        if (mate) critters.push({ kind: 'deer', ...mate });
      }
      continue;
    }
    // Squirrels in yards and parks, mostly rabbits on the farm.
    const [num, den] = p.biome.id === 'farm' ? [3, 4] : p.biome.id === 'park' ? [1, 4] : [1, 3];
    const kind: CritterKind =
      p.level >= RABBITS_FROM && rng.chance(num, den) ? 'rabbit' : 'squirrel';
    critters.push({ kind, ...c });
  }

  const name = levelName(
    rng,
    p.biome,
    p.overgrown ? OVERGROWN_ADJECTIVES : (ADJECTIVES_BY_BIOME[p.biome.id] ?? ADJECTIVES),
    L.art,
    style,
  );
  const baseFile: LevelFile = {
    id: p.id,
    name,
    seed: p.seed,
    startDir: (['N', 'E', 'S', 'W'] as const)[startDir],
    mowerRule: p.mowerRule,
    rows: toAsciiRows(p.width, L.art, start),
    critters,
    pickups: [],
    ...(p.moles ? { moles: p.moles } : {}),
    ...(falls.length > 0 ? { falls } : {}),
    ...(hills.length > 0 ? { hills } : {}),
    ...(p.balls ? { balls: p.balls } : {}),
  };

  // Must be beatable, without a single hit, by every required mower. Optional (wide) mowers that
  // can't fit or can't finish are excluded from the lawn instead ("too wide for this yard").
  const candidates = allowedMowers(p.mowerRule);
  const required = new Set(
    p.required ?? candidates.filter((id) => (MOWER_DEFS[id].cutWidth ?? 1) === 1),
  );
  const baseDef = loadLevel(baseFile);
  const routes = new Map<MowerId, { x: number; y: number; fuel: number }[]>();
  // A sane clear takes a few passes over the lawn; anything far longer is stuck (e.g. a cornered
  // critter sitting on the last grass), so give up quickly and try another layout.
  const maxTicks = (id: MowerId) => p.width * p.height * MOWERS[id].ticksPerTile * 6;
  const excluded: MowerId[] = [];
  for (const id of candidates) {
    const ok =
      Simulation.fits(baseDef.sim, MOWERS[id]) &&
      (() => {
        const r = solveGreedy(baseDef.sim, MOWERS[id], {
          ignoreFuel: true,
          maxTicks: maxTicks(id),
        });
        // Clean: no hits, and no flat tire (thorns are never needed to finish).
        const clean = r.won && r.crashes === 0 && r.flats === 0;
        if (clean) routes.set(id, r.route);
        return clean;
      })();
    if (!ok && required.has(id)) return null;
    if (!ok) excluded.push(id);
  }
  const mowers = candidates.filter((id) => !excluded.includes(id));
  if (mowers.length === 0) return null;
  const mowerRule: MowerRule =
    excluded.length === 0
      ? p.mowerRule
      : p.mowerRule.kind === 'only'
        ? { kind: 'only', mowers: mowers }
        : {
            kind: 'exclude',
            mowers: [...(p.mowerRule.kind === 'exclude' ? p.mowerRule.mowers : []), ...excluded],
          };

  const pickups = placePickups(p, L, rng, start, critters, mowers, routes);
  const file: LevelFile = { ...baseFile, mowerRule, pickups };
  const def = loadLevel(file);

  // Final check with real fuel tanks, and par times.
  const par: Partial<Record<MowerId, number>> = {};
  for (const id of mowers) {
    const r = solveGreedy(def.sim, MOWERS[id], { maxTicks: maxTicks(id) });
    if (!r.won || r.crashes > 0 || r.flats > 0) return null;
    par[id] = r.ticks;
  }
  const finalFile: LevelFile = {
    ...file,
    generatorVersion: GENERATOR_VERSION,
    level: p.level,
    biome: p.biome.id,
    par,
  };
  return { file: finalFile, def: loadLevel(finalFile) };
}

// ── Composition (campaign lawns, lm-66f) ────────────────────────────────────────────────────

/**
 * A campaign lawn's style (owner, 2026-10-06: "having both symmetric and somewhat not symmetric
 * is key"): `formal`, motifs mirrored left to right; `tidy`, motifs, not mirrored; `wild`, the
 * natural scatter, a field left to itself. Weights per zone: gardens and parks mixed, the course
 * and the farm leaning tidy or wild, the formal zones formal, the meadows wild.
 */
export type LawnStyle = 'formal' | 'tidy' | 'wild';
const STYLE_WEIGHTS: Record<string, Partial<Record<LawnStyle, number>>> = {
  backyard: { formal: 3, tidy: 3, wild: 4 },
  park: { formal: 4, tidy: 3, wild: 3 },
  farm: { formal: 3, tidy: 4, wild: 3 },
  golf: { formal: 3, tidy: 3, wild: 4 },
  cemetery: { formal: 8, tidy: 2 },
  estate: { formal: 1 },
  stadium: { formal: 1 },
  meadow: { wild: 1 },
};

function lawnStyle(p: GenParams, rng: Rng): LawnStyle {
  if (p.symmetric) return 'formal';
  return (
    weightedPick<LawnStyle>(rng, STYLE_WEIGHTS[p.biome.id] ?? { wild: 1 }, () => true) ?? 'wild'
  );
}

/**
 * How a lawn item is placed. `formal`: set out by hand, in rows, pairs and clusters (benches,
 * lamps, gnomes, headstones...). `natural`: wherever it falls (rocks, sticks, dead trees, hives).
 * Trees and shrubs are planted either way.
 */
const NATURAL_ITEMS: ReadonlySet<TileId> = new Set<TileId>([
  'rock',
  'sticks',
  'deadTree',
  'beehive',
]);

/** The most of one item in a row (bins and the like come in pairs, never rows). */
const ROW_CAP: Partial<Record<TileId, number>> = {
  bin: 0,
  birdBath: 2,
  trough: 2,
  ballWasher: 2,
  statue: 2,
  urn: 2,
  bench: 3,
  gnome: 3,
  lantern: 3,
  lampPost: 4,
  hayBale: 4,
  topiary: 4,
  cone: 5,
  tree: 5,
  shrub: 5,
  headstone: 6,
};

/** Terrain that grows on its own (irregular patches, never mirrored rectangles). */
const NATURAL_TERRAIN: ReadonlySet<TileId> = new Set<TileId>([
  'tallGrass',
  'mud',
  'meadowGrass',
  'water',
]);

/** The rectangles a planted bed may take (width, height). */
function bedShapes(id: TileId): readonly [number, number][] {
  switch (id) {
    case 'flowerBed':
    case 'crops':
      return [
        [2, 2],
        [3, 2],
        [2, 3],
        [4, 1],
        [1, 3],
      ];
    case 'wheat':
      return [
        [3, 3],
        [4, 2],
        [2, 4],
        [3, 2],
      ];
    default:
      return [
        [2, 2],
        [3, 2],
      ];
  }
}

/**
 * Lays a formal or tidy lawn's terrain and its hand-placed items as motifs (mirrored left to
 * right when formal), and returns the budget left for the natural filler (what grows or falls on
 * its own), which the scatter then spends:
 *
 * - planted terrain (flowers, crops, wheat): rectangular beds and fields (a matching pair of items
 *   at a bed's corners now and then), borders along the side fences, planting strips across;
 *   natural terrain (long grass, mud, wildflowers, ponds): irregular patches, never mirrored;
 * - hand-placed items: rows at a regular spacing (capped per item: no row of bins), pairs, small
 *   clusters, a centerpiece on the middle line.
 *
 * Each motif goes down whole or not at all, under the scatter's rules (never orthogonally next to
 * another obstacle; from the Cemetery on, no dead ends; the start kept clear).
 */
function composeLawn(
  p: GenParams,
  L: Layout,
  rng: Rng,
  prot: Protected,
  topRows: number,
  budget: number,
  mirror: boolean,
): number {
  const w = p.width;
  const h = p.height;
  const mid = Math.floor(w / 2);
  const xMax = mirror ? mid - 1 : w - 1;
  const yMin = topRows + 1;
  const yMax = h - 3;
  if (yMax < yMin) return budget;
  let terrainLeft = Math.floor(budget * 0.4);
  // The natural filler keeps a share of the items (more on tidy lawns, and in the graveyard).
  const naturalShare = p.biome.id === 'cemetery' ? 0.35 : mirror ? 0.25 : 0.35;
  const itemsBudget = budget - terrainLeft;
  const naturalBudget = Math.floor(itemsBudget * naturalShare);
  let itemsLeft = itemsBudget - naturalBudget;

  const twins = (cells: readonly [number, number][], mirrored = mirror): [number, number][] => {
    const all = mirrored
      ? [...cells, ...cells.map(([x, y]): [number, number] => [w - 1 - x, y])]
      : [...cells];
    const seen = new Set<string>();
    return all.filter(([x, y]) => {
      const k = `${x},${y}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  };
  const free = (x: number, y: number) =>
    L.inBounds(x, y) && y >= topRows && L.get(x, y) === 'grass' && !prot(x, y);
  const hard = (x: number, y: number) => {
    const k = L.kind(x, y);
    return L.inBounds(x, y) && (isSolid(k) || k === Tile.Brush || k === Tile.Thorns);
  };
  /** Terrain on every cell (and twins), or nothing. Returns the cost, or -1. */
  const lay = (cells: readonly [number, number][], id: TileId, mirrored = mirror): number => {
    const all = twins(cells, mirrored);
    const cost = all.length * TILE_BY_ID[id].cost;
    if (cost > terrainLeft || !all.every(([x, y]) => free(x, y))) return -1;
    for (const [x, y] of all) L.set(x, y, id);
    return cost;
  };
  /** Items on every cell (and twins), or none: never side by side with anything hard. */
  const put = (cells: readonly [number, number][], id: TileId): number => {
    const all = twins(cells);
    const cost = all.length * TILE_BY_ID[id].cost;
    if (cost > itemsLeft || !all.every(([x, y]) => free(x, y))) return -1;
    const set = new Set(all.map(([x, y]) => `${x},${y}`));
    const touching = all.some(([x, y]) =>
      DIRECTIONS.some((d) => {
        const nx = x + dx(d);
        const ny = y + dy(d);
        return set.has(`${nx},${ny}`) || hard(nx, ny);
      }),
    );
    if (touching) return -1;
    const placed: [number, number][] = [];
    for (const [x, y] of all) {
      if (p.level > 80 && makesDeadEnd(L, x, y)) {
        for (const [px, py] of placed) L.set(px, py, 'grass');
        return -1;
      }
      L.set(x, y, id);
      placed.push([x, y]);
    }
    return cost;
  };
  const pickTerrain = () =>
    weightedPick<TileId>(
      rng,
      p.biome.terrainWeights,
      (t) => unlocked(t, p.level) && !(p.overgrown && t === 'meadowGrass'),
    );
  const placeable = TILES.filter((t) => t.placement === 'scatter').map((t) => t.id);
  /** A hand-placed item (anything the zone has but what falls where it may). */
  const pickItem = () =>
    weightedPick<TileId>(
      rng,
      p.biome.scatterWeights,
      (t) =>
        placeable.includes(t) &&
        !NATURAL_ITEMS.has(t) &&
        unlocked(t, p.level) &&
        TILE_BY_ID[t].cost <= itemsLeft,
    );

  // Terrain.
  let beds = 0;
  for (let tries = 0; tries < 40 && beds < p.terrainPatches; tries++) {
    const id = pickTerrain();
    if (!id) break;
    if (NATURAL_TERRAIN.has(id)) {
      // Grows on its own: an irregular patch, one side only.
      const cx = rng.nextInt(w);
      const cy = rng.intBetween(yMin, yMax);
      const cells = patchShape(id, rng)
        .map(([ox, oy]): [number, number] => [cx + ox, cy + oy])
        .filter(([x, y]) => free(x, y));
      if (cells.length < 2) continue;
      const cost = lay(cells, id, false);
      if (cost < 0) continue;
      terrainLeft -= cost;
      beds++;
      continue;
    }
    // Planted: a bed or field, a border along the fence, or a strip across.
    const strips = id === 'flowerBed' || id === 'crops';
    const roll = rng.nextInt(100);
    let cells: [number, number][] = [];
    let rect: { x: number; y: number; w: number; h: number } | null = null;
    if (roll < 60 || (roll >= 85 && !strips)) {
      const [rw, rh] = rng.pick(bedShapes(id));
      const right = (mirror ? mid - 1 : w - 1) - rw;
      if (right < 1) continue;
      const x0 = rng.intBetween(1, right);
      const y0 = rng.intBetween(yMin, Math.max(yMin, yMax - rh));
      rect = { x: x0, y: y0, w: rw, h: rh };
      for (let y = y0; y < y0 + rh; y++) for (let x = x0; x < x0 + rw; x++) cells.push([x, y]);
    } else if (roll < 85) {
      const len = rng.intBetween(3, Math.min(6, yMax - yMin + 1));
      const y0 = rng.intBetween(yMin, yMax - len + 1);
      const side = mirror || rng.chance(1, 2) ? 0 : w - 1;
      cells = Array.from({ length: len }, (_, k): [number, number] => [side, y0 + k]);
    } else {
      const y = rng.intBetween(yMin, yMax);
      for (let x = 1; x <= xMax; x += 2) cells.push([x, y]);
      if (cells.length < 2) continue;
    }
    const cost = lay(cells, id);
    if (cost < 0) continue;
    terrainLeft -= cost;
    beds++;
    if (rect && rng.chance(2, 5)) {
      // A matching pair of items at the bed's corners (a gnome by the roses).
      const decor = pickItem();
      if (decor) {
        const corners: [number, number][] = rng.chance(1, 2)
          ? [
              [rect.x - 1, rect.y - 1],
              [rect.x + rect.w, rect.y + rect.h],
            ]
          : [
              [rect.x + rect.w, rect.y - 1],
              [rect.x - 1, rect.y + rect.h],
            ];
        const c = put(corners, decor);
        if (c > 0) itemsLeft -= c;
      }
    }
  }
  itemsLeft += terrainLeft;

  // Hand-placed items: rows (capped per item), pairs, clusters, a centerpiece.
  for (let tries = 0; tries < 80 && itemsLeft > 0; tries++) {
    const id = pickItem();
    if (!id) break;
    const cap = ROW_CAP[id] ?? 3;
    const roll = rng.nextInt(100);
    const y = rng.intBetween(yMin, yMax);
    let cells: [number, number][] = [];
    if (roll < 40 && cap >= 2) {
      // A row at a regular spacing, no longer than the item allows (both halves counted).
      const step = rng.intBetween(2, 3);
      const most = mirror ? Math.max(1, Math.floor(cap / 2)) : cap;
      for (
        let x = rng.intBetween(1, 2);
        x <= xMax - (mirror ? 1 : 0) && cells.length < most;
        x += step
      )
        cells.push([x, y]);
      if (cells.length < (mirror ? 1 : 2)) continue;
    } else if (roll < 65) {
      const x = rng.intBetween(1, Math.max(1, xMax - (mirror ? 1 : 3)));
      cells = mirror
        ? [[x, y]]
        : [
            [x, y],
            [x + 3, y],
          ];
    } else if (roll < 85 && cap >= 3) {
      const x = rng.intBetween(0, Math.max(0, xMax - 2));
      cells = [
        [x, y],
        [x + 2, y],
        [x + 1, y + 1],
      ];
    } else {
      cells = w % 2 === 1 ? [[mid, y]] : [[Math.max(1, mid - 2), y]];
    }
    const cost = put(cells, id);
    if (cost > 0) itemsLeft -= cost;
  }
  // Left for the natural filler: its share and whatever the motifs did not use.
  return naturalBudget + Math.max(0, itemsLeft);
}

// ── Structures (per biome) ──────────────────────────────────────────────────────────────────

type Protected = (x: number, y: number) => boolean;

/** Lays out the biome's fixed features; returns how many top rows they reserve. */
function buildStructures(p: GenParams, L: Layout, rng: Rng, prot: Protected): number {
  switch (p.biome.id) {
    case 'park':
      return buildPark(p, L, rng, prot);
    case 'farm':
      return buildFarm(p, L, rng, prot);
    case 'golf':
      return buildGolf(p, L, rng, prot);
    case 'cemetery':
      return buildCemetery(p, L, rng, prot);
    case 'estate':
      return buildEstate(p, L, rng, prot);
    case 'stadium':
      return buildStadium(p, L, prot);
    case 'meadow':
      return buildMeadow(p, L, rng, prot);
    default:
      return buildBackyard(p, L, rng);
  }
}

/** The house's back deck along the top edge with a patio below, and sometimes a garden path. */
function buildBackyard(p: GenParams, L: Layout, rng: Rng): number {
  let topRows = 0;
  if (unlocked('house', p.level) && p.height >= 9 && rng.chance(2, 3)) {
    const hw = rng.intBetween(3, Math.min(5, p.width - 4));
    const x0 = rng.chance(1, 2) ? 0 : p.width - hw;
    for (let y = 0; y < 2; y++) for (let x = x0; x < x0 + hw; x++) L.set(x, y, 'house');
    for (let x = x0; x < x0 + hw; x++) L.set(x, 2, 'path');
    topRows = 3;
  }
  if (unlocked('path', p.level) && rng.chance(1, 2)) {
    if (rng.chance(1, 2)) {
      const y = rng.intBetween(topRows + 2, p.height - 4);
      for (let x = 0; x < p.width; x++) if (L.get(x, y) === 'grass') L.set(x, y, 'path');
    } else {
      const x = rng.intBetween(2, p.width - 3);
      for (let y = topRows; y < p.height - 2; y++) if (L.get(x, y) === 'grass') L.set(x, y, 'path');
    }
  }
  return topRows;
}

/** Footpaths crossing the park, maybe a pond and a sandpit. */
function buildPark(p: GenParams, L: Layout, rng: Rng, prot: Protected): number {
  const across = rng.chance(2, 3);
  if (across) {
    const y = rng.intBetween(2, p.height - 4);
    for (let x = 0; x < p.width; x++) L.set(x, y, 'path');
  }
  if (!across || rng.chance(1, 2)) {
    const x = rng.intBetween(2, p.width - 3);
    for (let y = 0; y < p.height - 2; y++) if (L.get(x, y) === 'grass') L.set(x, y, 'path');
  }
  if (unlocked('water', p.level) && rng.chance(1, 2)) {
    block(L, rng, prot, 'water', rng.intBetween(2, 3), rng.intBetween(2, 3));
  }
  if (rng.chance(1, 2)) block(L, rng, prot, 'sand', rng.intBetween(2, 3), 2);
  return 0;
}

/** A barn and farmyard up top, fence lines with gates across the field, maybe a hedgerow. */
function buildFarm(p: GenParams, L: Layout, rng: Rng, prot: Protected): number {
  let topRows = 0;
  if (p.height >= 12) {
    const bw = rng.intBetween(4, 6);
    const x0 = rng.chance(1, 2) ? 0 : p.width - bw;
    for (let y = 0; y < 2; y++) for (let x = x0; x < x0 + bw; x++) L.set(x, y, 'barn');
    for (let x = x0; x < x0 + bw; x++) L.set(x, 2, 'path');
    topRows = 3;
  }
  // Fence lines, each with a two-tile gate, at least four rows apart.
  const fences: number[] = [];
  const wanted = p.height >= 17 ? 2 : 1;
  for (let tries = 0; tries < 20 && fences.length < wanted; tries++) {
    const y = rng.intBetween(topRows + 3, p.height - 5);
    if (fences.some((f) => Math.abs(f - y) < 4)) continue;
    fences.push(y);
    const gate = rng.intBetween(0, p.width - 2);
    L.reserved.add(y * p.width + gate).add(y * p.width + gate + 1);
    for (let x = 0; x < p.width; x++) {
      if (x === gate || x === gate + 1 || prot(x, y) || L.get(x, y) !== 'grass') continue;
      L.set(x, y, 'fence');
    }
  }
  if (unlocked('hedge', p.level) && rng.chance(2, 3)) {
    const x = rng.intBetween(1, p.width - 2);
    const y0 = rng.intBetween(topRows + 1, p.height - 7);
    const len = rng.intBetween(3, 5);
    for (let y = y0; y < y0 + len; y++) {
      if (L.get(x, y) === 'grass' && !prot(x, y)) L.set(x, y, 'hedge');
    }
  }
  return topRows;
}

/**
 * A putting green with its flag pin in the upper half (keep off: it costs coins, like a flower bed),
 * sand bunkers beside it, maybe a cart path and a pond.
 */
function buildGolf(p: GenParams, L: Layout, rng: Rng, prot: Protected): number {
  if (rng.chance(1, 2)) {
    const x = rng.chance(1, 2) ? 1 : p.width - 2;
    for (let y = 0; y < p.height - 2; y++) if (L.get(x, y) === 'grass') L.set(x, y, 'path');
  }
  // The green: 3 tall, 3 wide (or 4 with rounded corners), the flag in the middle.
  const gw = rng.intBetween(3, 4);
  const x0 = rng.intBetween(2, p.width - gw - 2);
  const y0 = rng.intBetween(1, Math.max(1, Math.floor(p.height / 2) - 3));
  for (let y = y0; y < y0 + 3; y++) {
    for (let x = x0; x < x0 + gw; x++) {
      const corner = gw === 4 && (x === x0 || x === x0 + gw - 1) && (y === y0 || y === y0 + 2);
      if (!corner && L.get(x, y) === 'grass' && !prot(x, y)) L.set(x, y, 'green');
    }
  }
  L.set(x0 + Math.floor(gw / 2), y0 + 1, 'flag');
  // Sand bunkers hugging the green.
  for (let b = rng.intBetween(1, 2); b > 0; b--) {
    const side = rng.pick([-1, 1]);
    let x = side < 0 ? x0 - 1 : x0 + gw;
    let y = y0 + rng.intBetween(0, 3);
    for (let k = rng.intBetween(2, 4); k > 0; k--) {
      if (L.get(x, y) === 'grass' && !prot(x, y)) L.set(x, y, 'sand');
      if (rng.chance(1, 2)) x += side;
      else y += 1;
    }
  }
  if (unlocked('water', p.level) && rng.chance(1, 2)) {
    block(L, rng, prot, 'water', rng.intBetween(2, 3), 2);
  }
  return 0;
}

/**
 * A big tree away from the edges, with plain lawn all round under its crown (reserved, so nothing
 * hides there but grass, and maybe a coin) and no other crown overlapping.
 */
function plantBigTree(
  L: Layout,
  rng: Rng,
  prot: Protected,
  topRows: number,
  mirrored = false,
): void {
  const spots = L.cells().filter(
    ({ x, y }) =>
      x >= 2 &&
      x < L.w - 2 &&
      y >= topRows + 2 &&
      y < L.h - 3 &&
      L.get(x, y) === 'grass' &&
      !prot(x, y) &&
      BIG_TREE_CROWN.every(
        ([ox, oy]) => L.get(x + ox, y + oy) === 'grass' && !prot(x + ox, y + oy),
      ),
  );
  // A mirrored pair needs both spots, and crowns that don't meet in the middle.
  const twin = (x: number) => L.w - 1 - x;
  const usable = mirrored
    ? spots.filter(
        ({ x, y }) => Math.abs(twin(x) - x) >= 7 && spots.some((s) => s.x === twin(x) && s.y === y),
      )
    : spots;
  if (usable.length === 0) return;
  const { x, y } = rng.pick(usable);
  for (const tx of mirrored ? [x, twin(x)] : [x]) {
    L.set(tx, y, 'bigTree');
    // Reserve the crown and a ring round it, so crowns never touch.
    for (let oy = -3; oy <= 3; oy++) {
      for (let ox = -3; ox <= 3; ox++) {
        if (L.inBounds(tx + ox, y + oy)) L.reserved.add((y + oy) * L.w + tx + ox);
      }
    }
  }
}

/**
 * The Old Cemetery Grounds, laid out with care: a two-tile gravel walk straight up the middle
 * (sometimes crossed by another), rows of headstones in mirrored pairs either side with lanes
 * between them, memorial flowers at the foot of a few, and old lanterns at the head of the walk.
 */
function buildCemetery(p: GenParams, L: Layout, rng: Rng, prot: Protected): number {
  const w = p.width;
  const h = p.height;
  const mid = Math.floor(w / 2);
  const walk = w % 2 === 0 ? [mid - 1, mid] : [mid];
  for (let y = 0; y < h - 2; y++) for (const x of walk) if (!prot(x, y)) L.set(x, y, 'path');
  // A cross walk about halfway down, now and then.
  let cross = -1;
  if (h >= 14 && rng.chance(1, 2)) {
    cross = rng.intBetween(Math.floor(h / 2) - 1, Math.floor(h / 2));
    for (let x = 0; x < w; x++) if (!prot(x, cross)) L.set(x, cross, 'path');
  }
  // Lanterns flank the head of the walk.
  for (const x of [walk[0]! - 1, walk[walk.length - 1]! + 1]) {
    if (L.get(x, 0) === 'grass' && !prot(x, 0)) L.set(x, 0, 'lantern');
  }
  // Rows of headstones every third row, every other tile, mirrored about the walk. A row now and
  // then is left empty, and the odd stone missing, so it reads as old rather than printed.
  const stoneCols: number[] = [];
  for (let x = walk[0]! - 2; x >= 1; x -= 2) stoneCols.push(x);
  const flowers = unlocked('flowerBed', p.level);
  let stones = 0;
  const free = (x: number, y: number) =>
    L.get(x, y) === 'grass' &&
    !prot(x, y) &&
    DIRECTIONS.every((d) => {
      const k = L.kind(x + dx(d), y + dy(d));
      return !L.inBounds(x + dx(d), y + dy(d)) || (!isSolid(k) && k !== Tile.Brush);
    });
  for (let y = rng.intBetween(2, 3); y < h - 4; y += 3) {
    if (y === cross || y === cross + 1 || y === cross - 1 || rng.chance(1, 5)) continue;
    for (const x of stoneCols) {
      if (rng.chance(1, 8)) continue;
      const mirror = w - 1 - x;
      if (!free(x, y) || !free(mirror, y)) continue;
      L.set(x, y, 'headstone');
      L.set(mirror, y, 'headstone');
      stones += 2;
      if (flowers && rng.chance(1, 6)) {
        for (const fx of [x, mirror]) {
          if (L.get(fx, y + 1) === 'grass' && !prot(fx, y + 1)) L.set(fx, y + 1, 'flowerBed');
        }
      }
    }
  }
  L.spent += stones * 2;
  return 0;
}

/**
 * The Grand Estate's formal gardens, mirrored about the middle, laid out to one of three plans
 * (owner, 2026-10-06: not the same avenue and river every time):
 *
 * - avenue: a two-tile cobblestone avenue up the middle round a reflecting pool, topiary pairs
 *   along it;
 * - twin walks: a walk up each side joined across the top and bottom, a lawn between them (with
 *   a pond in the middle on a tall lawn);
 * - crossing: the avenue crossed by a walk, with a fountain where they meet.
 *
 * Statues stand at the head of the walks (or a terrace with urns along the top, when the lawn has
 * one). A river, one or two rows wide and set at a varying height, crosses the grounds, bridged
 * wherever a walk meets it and now and then at a pair more. Returns the rows the terrace takes.
 */
function buildEstate(p: GenParams, L: Layout, rng: Rng, prot: Protected): number {
  const w = p.width;
  const h = p.height;
  const mid = w / 2; // lawns here are an even width: the avenue is columns mid-1 and mid
  const both = (x: number, y: number, id: TileId) => {
    for (const tx of [x, w - 1 - x]) if (L.get(tx, y) === 'grass' && !prot(tx, y)) L.set(tx, y, id);
  };
  const plan = rng.pick(['avenue', 'twin', 'crossing'] as const);
  let topRows = 0;
  if (p.patio) {
    // A terrace across the top middle, urns at its corners.
    const half = rng.intBetween(3, 4);
    for (let y = 0; y < 2; y++) for (let x = mid - half; x < mid; x++) both(x, y, 'cobble');
    for (const y of rng.chance(1, 2) ? [0, 1] : [1]) {
      L.set(mid - half, y, 'urn');
      L.set(w - 1 - (mid - half), y, 'urn');
    }
    topRows = 2;
  }
  /** The columns the walks run down (for the bridges). */
  const walks: number[] = [];
  if (plan === 'twin') {
    const side = rng.intBetween(2, Math.max(2, mid - 4));
    walks.push(side, w - 1 - side);
    for (let y = topRows; y < h - 2; y++) both(side, y, 'cobble');
    // Joined across the top and near the bottom.
    for (const y of [topRows, h - 3]) for (let x = side; x < mid; x++) both(x, y, 'cobble');
    if (!p.patio) both(side - 1, 0, 'statue');
    if (h >= 15) {
      const py = Math.floor(h / 2) - 1;
      for (let y = py; y < py + 2; y++) {
        L.set(mid - 1, y, 'water');
        L.set(mid, y, 'water');
      }
    }
  } else {
    walks.push(mid - 1, mid);
    for (let y = topRows; y < h - 2; y++) both(mid - 1, y, 'cobble');
    if (!p.patio) both(mid - 2, 0, 'statue');
    if (plan === 'avenue' && h >= 15) {
      // A reflecting pool in the middle of the avenue, the walk going round it.
      const py = rng.intBetween(Math.floor(h / 2) - 3, Math.floor(h / 2) - 1);
      for (let y = py; y < py + 3; y++) {
        L.set(mid - 1, y, 'water');
        L.set(mid, y, 'water');
        both(mid - 2, y, 'cobble');
      }
      for (const y of [py - 1, py + 3]) both(mid - 2, y, 'cobble');
    }
    if (plan === 'crossing') {
      // A cross walk, and a fountain where the two meet, the walk going round it.
      const cy = rng.intBetween(Math.floor(h / 2) - 1, Math.floor(h / 2) + 1);
      for (let x = 0; x < mid; x++) both(x, cy, 'cobble');
      L.set(mid - 1, cy, 'fountain');
      L.set(mid, cy, 'fountain');
      for (const y of [cy - 1, cy + 1]) both(mid - 2, y, 'cobble');
    }
    // Topiary pairs along the avenue, a lane's width off it.
    for (let y = topRows + 2; y < h - 4; y += 4) {
      if (rng.chance(1, 3)) continue;
      const x = mid - 4;
      const twinOk = L.get(x, y) === 'grass' && !prot(x, y) && !makesDeadEnd(L, x, y);
      if (twinOk) both(x, y, 'topiary');
    }
  }
  // The river: one or two rows across, bridged on the walks and now and then a pair more.
  if (p.river) {
    const rows = rng.intBetween(1, 2);
    // Clear of the pool, the fountain and the cross walk (rows with anything but grass and the
    // walks' own cobbles on them).
    const busyRow = (y: number) =>
      [...Array(w).keys()].some((x) => {
        const id = L.get(x, y);
        return id !== 'grass' && !(id === 'cobble' && walks.includes(x));
      });
    let ry = -1;
    for (let tries = 0; tries < 12 && ry < 0; tries++) {
      const y = rng.intBetween(topRows + 3, Math.max(topRows + 3, h - 6 - rows));
      if (![...Array(rows + 2).keys()].some((k) => busyRow(y - 1 + k))) ry = y;
    }
    if (ry < 0) return topRows;
    const extra = rng.chance(1, 2) ? rng.intBetween(1, Math.max(1, mid - 3)) : -1;
    const bridged = (x: number) =>
      walks.includes(x) ||
      (walks.length === 2 && walks[0] === mid - 1 && (x === mid - 1 || x === mid)) ||
      x === extra ||
      x === w - 1 - extra;
    for (let y = ry; y < ry + rows; y++) {
      for (let x = 0; x < w; x++) {
        if (prot(x, y)) continue;
        // (A fountain or pool tile on the river's line stays as it is.)
        if (L.get(x, y) === 'fountain' || L.get(x, y) === 'water') continue;
        L.set(x, y, bridged(x) || L.get(x, y) === 'cobble' ? 'bridge' : 'water');
      }
    }
  }
  return topRows;
}

/**
 * Rolling Meadows: open country. Its grand trees, rolling hills, wildflower patches and wildlife
 * come with the shared stages; here, a creek wanders across the meadow, a tile wide, crossed by
 * three footbridges.
 */
function buildMeadow(p: GenParams, L: Layout, rng: Rng, prot: Protected): number {
  if (!p.creek) return 0;
  const w = p.width;
  const h = p.height;
  // West to east, stepping up or down a row now and then (both tiles of a step are water, so
  // the creek never leaks through a corner).
  let y = rng.intBetween(Math.floor(h * 0.3), Math.floor(h * 0.5));
  const cells: [number, number][] = [];
  for (let x = 0; x < w; x++) {
    cells.push([x, y]);
    if (x < w - 1 && rng.chance(1, 3)) {
      const ny = clamp(y + (rng.chance(1, 2) ? 1 : -1), 3, h - 6);
      if (ny !== y) {
        cells.push([x, ny]);
        y = ny;
      }
    }
  }
  for (const [x, cy] of cells) if (!prot(x, cy)) L.set(x, cy, 'water');
  // Footbridges where it runs straight (one water tile in the column), spread across the meadow.
  const straight = (x: number) => cells.filter(([cx]) => cx === x).length === 1;
  for (const target of [Math.floor(w / 5), Math.floor(w / 2), Math.floor((4 * w) / 5)]) {
    for (let d = 0; d < w; d++) {
      const x = [target - d, target + d].find((c) => c >= 1 && c < w - 1 && straight(c));
      if (x === undefined) continue;
      const cell = cells.find(([cx]) => cx === x)!;
      L.set(cell[0], cell[1], 'bridge');
      break;
    }
  }
  return 0;
}

/**
 * A pitch, marked out: a halfway line across the middle, a penalty box before each goal, the
 * goals at either end and flags in the top corners. Lines are grass with chalk on (mowed like any). Mirrored like everything else here.
 */
function buildStadium(p: GenParams, L: Layout, prot: Protected): number {
  const w = p.width;
  const h = p.height;
  const mid = w / 2;
  const line = (x: number, y: number) => {
    if (L.get(x, y) === 'grass' && !prot(x, y)) L.set(x, y, 'fieldLine');
  };
  // Goals at both ends, centered (the bottom one clear of the start).
  for (const y of [0, h - 1]) {
    for (let x = mid - 2; x < mid + 2; x++) if (!prot(x, y)) L.set(x, y, 'goal');
  }
  for (const x of [0, w - 1]) if (!prot(x, 0)) L.set(x, 0, 'cornerFlag');
  // Penalty boxes: a rectangle before each goal.
  const bw = Math.min(4, mid - 2);
  for (const [y0, dy] of [
    [1, 1],
    [h - 2, -1],
  ] as const) {
    const depth = 3;
    for (let k = 0; k < depth; k++) {
      line(mid - 1 - bw, y0 + k * dy);
      line(mid + bw, y0 + k * dy);
    }
    for (let x = mid - 1 - bw; x <= mid + bw; x++) line(x, y0 + (depth - 1) * dy);
  }
  // The halfway line (the center circle is drawn round its middle by the lawn view).
  const cy = Math.floor(h / 2);
  for (let x = 0; x < w; x++) line(x, cy);
  return 0;
}

/**
 * Hills: grassy mounds on open lawn (mirrored on a formal lawn), 3 to 5 tiles across with odd
 * sizes so each has a middle. Returns their specs for the level file.
 */
function layHills(
  p: GenParams,
  L: Layout,
  rng: Rng,
  topRows: number,
): NonNullable<LevelFile['hills']> {
  const out: NonNullable<LevelFile['hills']> = [];
  const taken = (x: number, y: number, w: number, h: number) =>
    out.some((o) => x < o.x + o.w + 1 && x + w + 1 > o.x && y < o.y + o.h + 1 && y + h + 1 > o.y);
  for (let n = 0; n < (p.hills ?? 0); n++) {
    for (let tries = 0; tries < 200; tries++) {
      const size = p.bigHills
        ? rng.pick(tries < 100 ? [7, 5, 7] : [5, 3])
        : rng.pick(tries < 100 ? [5, 5, 3] : [3]);
      const w = size;
      const h = size;
      // (A mirrored lawn lays its hills on the left and copies them across.)
      const x = rng.intBetween(0, Math.max(0, p.symmetric ? Math.floor(L.w / 2) - w - 1 : L.w - w));
      const y = rng.intBetween(topRows + 1, Math.max(topRows + 1, L.h - h - 3));
      if (taken(x, y, w, h)) continue;
      // On open lawn: nearly all of it grass, none of it under a tree's crown.
      let grass = 0;
      for (let ty = y; ty < y + h; ty++) {
        for (let tx = x; tx < x + w; tx++) {
          const open = L.get(tx, ty) === 'grass' || L.get(tx, ty) === 'tallGrass';
          if (open && !L.reserved.has(ty * L.w + tx)) grass++;
        }
      }
      if (grass < w * h - 1) continue;
      out.push({ x, y, w, h });
      if (p.symmetric) {
        const mx = L.w - x - w;
        if (!taken(mx, y, w, h)) out.push({ x: mx, y, w, h });
      }
      break;
    }
  }
  return out;
}

/**
 * Lights Out: old lanterns spread evenly over the lawn, one to each band of rows, alternating
 * sides, on open grass that leaves no dead end.
 */
function spreadLanterns(L: Layout, rng: Rng, prot: Protected, count: number): void {
  const band = L.h / count;
  for (let k = 0; k < count; k++) {
    const y0 = Math.floor(k * band);
    const y1 = Math.max(y0, Math.floor((k + 1) * band) - 1);
    const left = k % 2 === 0;
    const spots = L.cells().filter(
      ({ x, y }) =>
        y >= y0 &&
        y <= y1 &&
        (left ? x < L.w / 2 : x >= L.w / 2) &&
        x > 0 &&
        x < L.w - 1 &&
        L.get(x, y) === 'grass' &&
        !prot(x, y) &&
        DIRECTIONS.every(
          (d) => !isSolid(L.kind(x + dx(d), y + dy(d))) || !L.inBounds(x + dx(d), y + dy(d)),
        ) &&
        !makesDeadEnd(L, x, y),
    );
    if (spots.length === 0) continue;
    const s = rng.pick(spots);
    L.set(s.x, s.y, 'lantern');
  }
}

/**
 * A thorn patch: a small tangle (three to five tiles) on plain grass. It is placed like an
 * obstacle, since a mower keeps off it: nothing solid touching it, no dead end beside it, and the
 * lawn still in one piece with every thorn patch (and falling tree's log) counted as a wall.
 */
function thornPatch(
  L: Layout,
  rng: Rng,
  prot: Protected,
  topRows: number,
  start: { x: number; y: number },
  mirrored = false,
): void {
  const shapes: [number, number][][] = [
    [
      [0, 0],
      [1, 0],
      [0, 1],
    ],
    [
      [0, 0],
      [0, 1],
      [0, 2],
      [1, 1],
    ],
    [
      [0, 0],
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ],
  ];
  // Tight lawns: a small tangle will do.
  const small: [number, number][][] = [
    [
      [0, 0],
      [1, 0],
    ],
    [[0, 0]],
  ];
  const thorns = new Set<number>();
  L.cells().forEach(({ x, y }) => {
    if (L.get(x, y) === 'thorns') thorns.add(y * L.w + x);
  });
  const cells = rng.shuffle(L.cells().filter(({ y }) => y >= topRows));
  // On a mirrored lawn the patch and its twin go in together (well apart); a single one only if
  // no pair fits anywhere.
  const tries = mirrored
    ? [
        ...cells.slice(0, 200).map((c) => ({ c, twin: true })),
        ...cells.slice(0, 120).map((c) => ({ c, twin: false })),
      ]
    : cells.slice(0, 120).map((c) => ({ c, twin: false }));
  for (const [k, { c, twin }] of tries.entries()) {
    const one = rng
      .pick(k < tries.length / 2 ? shapes : small)
      .map(([ox, oy]) => ({ x: c.x + ox, y: c.y + oy }));
    const shape = twin ? [...one, ...one.map((t) => ({ x: L.w - 1 - t.x, y: t.y }))] : one;
    if (twin && one.some((t) => Math.abs(L.w - 1 - t.x - t.x) <= 2)) continue;
    const inPatch = (x: number, y: number) => shape.some((t) => t.x === x && t.y === y);
    if (shape.some((t) => L.get(t.x, t.y) !== 'grass' || prot(t.x, t.y))) continue;
    // Around it: open lawn, never another obstacle or patch, and no dead ends.
    const blocked = (x: number, y: number) =>
      inPatch(x, y) || thorns.has(y * L.w + x) || !L.open(x, y);
    const ok = shape.every((t) =>
      DIRECTIONS.every((d) => {
        const nx = t.x + dx(d);
        const ny = t.y + dy(d);
        if (inPatch(nx, ny) || !L.inBounds(nx, ny)) return true;
        if (blocked(nx, ny)) return false;
        const exits = DIRECTIONS.filter((e) => !blocked(nx + dx(e), ny + dy(e))).length;
        return exits >= 2;
      }),
    );
    if (!ok) continue;
    const walls = new Set([...L.fallZone, ...thorns, ...shape.map((t) => t.y * L.w + t.x)]);
    if (!openInOnePiece(L, start, walls)) continue;
    for (const t of shape) L.set(t.x, t.y, 'thorns');
    return;
  }
}

/**
 * A dead tree that will fall during play: on grass, falling three tiles over plain lawn that
 * stays open until then. It must never cut the lawn in two: with every log down, all the open
 * lawn is still one piece. Reserves its tile and where it lands. Returns its fall, or null.
 */
function placeFallingTree(
  L: Layout,
  rng: Rng,
  prot: Protected,
  start: { x: number; y: number },
  topRows: number,
  mirrored = false,
): NonNullable<LevelFile['falls']> {
  const letters = ['N', 'E', 'S', 'W'] as const;
  const options: { x: number; y: number; d: Direction }[] = [];
  for (const { x, y } of L.cells()) {
    if (y < topRows || L.get(x, y) !== 'grass' || prot(x, y)) continue;
    if (
      DIRECTIONS.some(
        (d) => isSolid(L.kind(x + dx(d), y + dy(d))) && L.inBounds(x + dx(d), y + dy(d)),
      )
    )
      continue;
    for (const d of DIRECTIONS) {
      const tiles = fallTiles({ x, y, dir: d, at: 1, length: 3 });
      const ok = tiles.every(
        (t) =>
          L.inBounds(t.x, t.y) &&
          (L.get(t.x, t.y) === 'grass' || L.get(t.x, t.y) === 'tallGrass') &&
          !prot(t.x, t.y) &&
          !L.fallZone.has(t.y * L.w + t.x),
      );
      if (ok) options.push({ x, y, d });
    }
  }
  const mirrorDir = (d: Direction): Direction => (d === Dir.E ? Dir.W : d === Dir.W ? Dir.E : d);
  const isOption = (x: number, y: number, d: Direction) =>
    options.some((o) => o.x === x && o.y === y && o.d === d);
  for (const o of rng.shuffle(options).slice(0, 24)) {
    const trees = [o];
    if (mirrored) {
      const twin = { x: L.w - 1 - o.x, y: o.y, d: mirrorDir(o.d) };
      if (Math.abs(twin.x - o.x) < 4 || !isOption(twin.x, twin.y, twin.d)) continue;
      trees.push(twin);
    }
    const tiles = trees.flatMap((t) => fallTiles({ x: t.x, y: t.y, dir: t.d, at: 1, length: 3 }));
    const logs = new Set([...L.fallZone, ...tiles.map((t) => t.y * L.w + t.x)]);
    if (!openInOnePiece(L, start, logs)) continue;
    const at = rng.intBetween(14, 40);
    for (const t of trees) {
      L.set(t.x, t.y, 'deadTree');
      L.reserved.add(t.y * L.w + t.x);
    }
    for (const t of tiles) {
      L.fallZone.add(t.y * L.w + t.x);
      L.reserved.add(t.y * L.w + t.x);
    }
    // Twins fall a moment apart.
    return trees.map((t, i) => ({ x: t.x, y: t.y, dir: letters[t.d], at: at + i * 6, length: 3 }));
  }
  return [];
}

/**
 * Whether an obstacle at (x, y) would leave a dead end: an open tile beside it with only one way
 * out (a mower that can't reverse can never mow it and leave).
 */
function makesDeadEnd(L: Layout, x: number, y: number): boolean {
  // Thorn patches count as closed too: a mower keeps off them.
  const open = (ox: number, oy: number) => L.open(ox, oy) && L.get(ox, oy) !== 'thorns';
  return DIRECTIONS.some((d) => {
    const nx = x + dx(d);
    const ny = y + dy(d);
    if (!open(nx, ny)) return false;
    const exits = DIRECTIONS.filter((e) => {
      const ex = nx + dx(e);
      const ey = ny + dy(e);
      return !(ex === x && ey === y) && open(ex, ey);
    }).length;
    return exits < 2;
  });
}

/** Whether every open tile is reachable from the start with these tiles blocked too. */
function openInOnePiece(L: Layout, start: { x: number; y: number }, blocked: Set<number>): boolean {
  const open = (x: number, y: number) => L.open(x, y) && !blocked.has(y * L.w + x);
  const seen = new Set<number>([start.y * L.w + start.x]);
  const queue = [start];
  while (queue.length > 0) {
    const c = queue.pop()!;
    for (const d of DIRECTIONS) {
      const nx = c.x + dx(d);
      const ny = c.y + dy(d);
      const i = ny * L.w + nx;
      if (!open(nx, ny) || seen.has(i)) continue;
      seen.add(i);
      queue.push({ x: nx, y: ny });
    }
  }
  return L.cells().every(({ x, y }) => !open(x, y) || seen.has(y * L.w + x));
}

/** Fills a w x h block of grass at a random spot (skipping protected and non-grass tiles). */
function block(L: Layout, rng: Rng, prot: Protected, id: TileId, w: number, h: number): void {
  const x0 = rng.intBetween(0, L.w - w);
  const y0 = rng.intBetween(0, L.h - h - 1);
  for (let y = y0; y < y0 + h; y++) {
    for (let x = x0; x < x0 + w; x++) if (L.get(x, y) === 'grass' && !prot(x, y)) L.set(x, y, id);
  }
}

const PLUS: readonly [number, number][] = [
  [0, 0],
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

/** The cells of one terrain patch, relative to its anchor. */
function patchShape(id: TileId, rng: Rng): [number, number][] {
  switch (id) {
    case 'water':
      return [
        [0, 0],
        [1, 0],
        [0, 1],
        [1, 1],
      ];
    case 'crops': {
      // A vegetable row.
      const len = rng.intBetween(3, 5);
      return Array.from({ length: len }, (_, i): [number, number] => [i, 0]);
    }
    case 'wheat': {
      // A small field.
      const out: [number, number][] = [];
      for (let y = 0; y < 2; y++) for (let x = 0; x < 3; x++) out.push([x, y]);
      return out;
    }
    default:
      return PLUS.filter(() => rng.chance(3, 4));
  }
}

// ── Pickups ─────────────────────────────────────────────────────────────────────────────────

function placePickups(
  p: GenParams,
  L: Layout,
  rng: Rng,
  start: { x: number; y: number },
  critters: readonly CritterSpawn[],
  mowers: readonly MowerId[],
  routes: ReadonlyMap<MowerId, { x: number; y: number; fuel?: number }[]>,
): PickupSpawn[] {
  const taken = new Set<string>([`${start.x},${start.y}`, ...critters.map((c) => `${c.x},${c.y}`)]);
  const pickups: PickupSpawn[] = [];
  const add = (kind: PickupKind, x: number, y: number) => {
    taken.add(`${x},${y}`);
    pickups.push({ kind, x, y });
  };
  // Nothing where a falling tree will land (it would be buried), and nothing on sticks (a riding
  // mower's route plows through them, but a weaker mower could never reach a can there).
  const free = (x: number, y: number) =>
    !taken.has(`${x},${y}`) &&
    !L.fallZone.has(y * L.w + x) &&
    L.kind(x, y) !== Tile.Brush &&
    !isSolid(L.kind(x, y));

  // Gas cans along each fueled mower's route, before the tank would run low.
  for (const id of mowers) {
    const cap = MOWER_DEFS[id].fuelTiles;
    const route = routes.get(id);
    if (cap === undefined || !route) continue;
    const existing = pickups.filter((q) => q.kind === 'gas');
    for (const c of planGasCans(route, cap, existing, free)) add('gas', c.x, c.y);
  }

  // Coins: about half on paths/mud (detours for the "all coins" bonus), the rest on grass.
  const detourCells = rng.shuffle(
    L.cells().filter(({ x, y }) => {
      const k = L.kind(x, y);
      return (k === Tile.Ground || k === Tile.Mud) && free(x, y);
    }),
  );
  // On overgrown lawns coins may sit in the long grass too (there is little else).
  const grassCells = rng.shuffle(
    L.cells().filter(
      ({ x, y }) =>
        (L.kind(x, y) === Tile.Grass || (!!p.overgrown && L.get(x, y) === 'tallGrass')) &&
        free(x, y),
    ),
  );
  const detourCoins = Math.min(detourCells.length, Math.ceil(p.coins / 2));
  for (const c of detourCells.slice(0, detourCoins)) add('coin', c.x, c.y);
  for (const c of grassCells) {
    if (pickups.filter((q) => q.kind === 'coin').length >= p.coins) break;
    if (free(c.x, c.y)) add('coin', c.x, c.y);
  }

  // A rare find: on a detour tile if there is one, otherwise the grass farthest from the start.
  if (p.rare) {
    const spot =
      detourCells.find((c) => free(c.x, c.y)) ??
      [...grassCells]
        .filter((c) => free(c.x, c.y))
        .sort((a, b) => dist(b, start) - dist(a, start))[0];
    if (spot) add('rare', spot.x, spot.y);
  }
  if (p.repair) {
    const spot = grassCells.find((c) => free(c.x, c.y));
    if (spot) add('repair', spot.x, spot.y);
  }
  return pickups;
}

/**
 * Where to drop gas cans along a route so a tank of `capacity` tiles never runs dry: whenever the
 * tank would fall to 30%, place a can on the next free route tile. Existing cans refill too. Each
 * step burns its recorded `fuel` (default 1; more through tall grass).
 */
export function planGasCans(
  route: readonly { x: number; y: number; fuel?: number }[],
  capacity: number,
  existing: readonly { x: number; y: number }[] = [],
  isFree: (x: number, y: number) => boolean = () => true,
): { x: number; y: number }[] {
  const cans: { x: number; y: number }[] = [];
  const isCan = (x: number, y: number) =>
    [...existing, ...cans].some((c) => c.x === x && c.y === y);
  let fuel = capacity;
  for (let i = 1; i < route.length; i++) {
    const { x, y, fuel: burned = 1 } = route[i] as { x: number; y: number; fuel?: number };
    fuel -= burned;
    if (isCan(x, y)) fuel = capacity;
    else if (fuel <= Math.floor(capacity * 0.3) && isFree(x, y)) {
      cans.push({ x, y });
      fuel = capacity;
    }
  }
  return cans;
}

const dist = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  Math.abs(a.x - b.x) + Math.abs(a.y - b.y);

// ── Names ───────────────────────────────────────────────────────────────────────────────────

const ADJECTIVES = [
  'Sunny',
  'Shady',
  'Breezy',
  'Dewy',
  'Mossy',
  'Quiet',
  'Sleepy',
  'Lucky',
  'Tidy',
  'Wild',
  'Golden',
  'Clover',
  'Hidden',
  'Crooked',
  'Lazy',
  'Busy',
  'Windy',
  'Misty',
  'Merry',
  'Daisy',
] as const;
const NOUNS = [
  'Patch',
  'Corner',
  'Yard',
  'Lawn',
  'Garden',
  'Plot',
  'Meadow',
  'Nook',
  'Green',
  'Lot',
  'Backyard',
  'Strip',
  'Square',
  'Hollow',
  'Commons',
  'Field',
  'Glade',
  'Spot',
  'Run',
  'Acre',
] as const;

const NOUNS_BY_BIOME: Partial<Record<BiomeDef['id'], readonly string[]>> = {
  park: ['Park', 'Commons', 'Green', 'Square', 'Gardens', 'Grove', 'Playground', 'Promenade'],
  farm: ['Field', 'Acre', 'Paddock', 'Pasture', 'Orchard', 'Meadow', 'Farmstead', 'Croft'],
  golf: ['Fairway', 'Links', 'Tee', 'Dogleg', 'Hole', 'Green', 'Course', 'Range'],
  cemetery: ['Grounds', 'Hollow', 'Glen', 'Gate', 'Knoll', 'Rest', 'Lane', 'Walk'],
  estate: ['Gardens', 'Lawns', 'Terrace', 'Parterre', 'Avenue', 'Grounds', 'Promenade', 'Court'],
  stadium: ['Pitch', 'Field', 'Park', 'Arena', 'Ground', 'Stadium', 'Bowl', 'Turf'],
  meadow: ['Meadow', 'Pasture', 'Downs', 'Vale', 'Lea', 'Fields', 'Hillside', 'Brook'],
};

/** Some worlds have their own mood in their names. */
const ADJECTIVES_BY_BIOME: Partial<Record<BiomeDef['id'], readonly string[]>> = {
  cemetery: ['Misty', 'Moonlit', 'Creaky', 'Quiet', 'Foggy', 'Mossy', 'Whispering', 'Old', 'Ivy'],
  estate: ['Grand', 'Royal', 'Stately', 'Elegant', 'Gilded', 'Noble', 'Regal', 'Fine', 'Formal'],
  stadium: ['Home', 'Victory', 'Center', 'Champions', 'Golden', 'Rally', 'Final', 'Derby', 'Cup'],
  meadow: ['Rolling', 'Sunny', 'Wildflower', 'Golden', 'Clover', 'Breezy', 'Honey', 'Willow'],
};

/** Overgrown lawns get their own adjectives, so the name says what you are in for. */
const OVERGROWN_ADJECTIVES = [
  'Overgrown',
  'Shaggy',
  'Unkempt',
  'Weedy',
  'Neglected',
  'Untamed',
  'Bushy',
] as const;

/**
 * Words that promise something on the lawn (owner, 2026-10-06: "Daisy Patch" had no flowers): each
 * is only used where the lawn has it. Words not listed fit anywhere.
 */
const NAME_NEEDS: Readonly<Record<string, (art: readonly TileId[], style: LawnStyle) => boolean>> =
  (() => {
    const count = (art: readonly TileId[], ids: readonly TileId[]) =>
      art.filter((id) => ids.includes(id)).length;
    const flowers = (art: readonly TileId[]) => count(art, ['flowerBed']) >= 2;
    const trees = (art: readonly TileId[]) => count(art, ['tree', 'bigTree']) >= 3;
    const water = (art: readonly TileId[]) => art.includes('water');
    const wild = (art: readonly TileId[], style: LawnStyle) =>
      style === 'wild' || count(art, ['tallGrass', 'sticks', 'meadowGrass']) >= 6;
    const tidy = (art: readonly TileId[], style: LawnStyle) =>
      style !== 'wild' && count(art, ['tallGrass', 'sticks']) <= 2;
    const busy = (art: readonly TileId[]) =>
      art.filter((id) => id !== 'grass' && id !== 'tallGrass').length >= art.length / 6;
    return {
      Daisy: flowers,
      Wildflower: (art) => flowers(art) || art.includes('meadowGrass'),
      Garden: flowers,
      Gardens: flowers,
      Parterre: (art) => flowers(art) || art.includes('topiary'),
      Orchard: trees,
      Grove: trees,
      Glade: trees,
      Shady: trees,
      Willow: trees,
      Brook: water,
      Wild: wild,
      Untamed: wild,
      Tidy: tidy,
      Formal: (_art, style) => style === 'formal',
      Busy: busy,
      Playground: (art) => art.includes('sand'),
      Avenue: (art) => art.includes('cobble'),
      Promenade: (art) => art.includes('path') || art.includes('cobble'),
    };
  })();

function levelName(
  rng: Rng,
  biome: BiomeDef,
  adjectives: readonly string[] = ADJECTIVES,
  art: readonly TileId[] = [],
  style: LawnStyle = 'wild',
): string {
  const fits = (word: string) => NAME_NEEDS[word]?.(art, style) ?? true;
  const nouns = NOUNS_BY_BIOME[biome.id] ?? NOUNS;
  const pick = (words: readonly string[]) => {
    const ok = words.filter(fits);
    return rng.pick(ok.length > 0 ? ok : words);
  };
  return `${pick(adjectives)} ${pick(nouns)}`;
}

/**
 * A garden hose (owner, 2026-10-06: hoses have to make sense): from a tap at the fence it heads
 * straight in, then runs in long straight lengths with a bend or two to water something, a flower
 * bed or crops where the lawn has them, else a spot out on the lawn. It never runs along the
 * fence, never doubles back beside itself, and keeps well clear of any other hose.
 */
function layHose(L: Layout, rng: Rng, protectedTile: (x: number, y: number) => boolean): void {
  const { w, h } = L;
  const free = (x: number, y: number) => L.get(x, y) === 'grass' && !protectedTile(x, y);
  const inner = (x: number, y: number) => x >= 1 && x <= w - 2 && y >= 1 && y <= h - 2;
  const others = L.cells().filter(({ x, y }) => L.get(x, y) === 'hose');
  const clearOfOthers = (x: number, y: number, by: number) =>
    others.every((q) => Math.abs(q.x - x) + Math.abs(q.y - y) >= by);
  // Taps: fence tiles with open lawn straight inside.
  const taps = L.cells().filter(({ x, y }) => {
    if (!(x === 0 || x === w - 1 || y === 0) || y >= h - 2 || !free(x, y)) return false;
    const ix = x === 0 ? 1 : x === w - 1 ? w - 2 : x;
    const iy = y === 0 ? 1 : y;
    return free(ix, iy) && clearOfOthers(x, y, 6);
  });
  // What it waters: lawn beside a flower bed or crops.
  const planted = (x: number, y: number) => {
    const id = L.get(x, y);
    return id === 'flowerBed' || id === 'crops';
  };
  const beside = L.cells().filter(
    ({ x, y }) =>
      inner(x, y) &&
      free(x, y) &&
      DIRECTIONS.some((d) => planted(x + dx(d), y + dy(d))) &&
      clearOfOthers(x, y, 4),
  );
  for (let attempt = 0; attempt < 12 && taps.length > 0; attempt++) {
    const tap = rng.pick(taps);
    const inDir: Direction = tap.y === 0 ? Dir.S : tap.x === 0 ? Dir.E : Dir.W;
    const near = (q: { x: number; y: number }) => {
      const d = Math.abs(q.x - tap.x) + Math.abs(q.y - tap.y);
      return d >= 3 && d <= 9;
    };
    const goals = beside.filter(near);
    const goal =
      goals.length > 0
        ? rng.pick(goals)
        : rng.pick(
            L.cells().filter(
              (q) => inner(q.x, q.y) && free(q.x, q.y) && near(q) && clearOfOthers(q.x, q.y, 4),
            ),
          );
    if (!goal) continue;
    const path = hoseRoute(
      L,
      tap,
      inDir,
      goal,
      (x, y) => inner(x, y) && free(x, y) && clearOfOthers(x, y, 3),
    );
    if (!path || path.length < 4 || path.length > 11) continue;
    // Never beside itself (other than the tile before and after).
    const at = new Map(path.map((q, k) => [`${q.x},${q.y}`, k]));
    const touches = path.some((q, k) =>
      DIRECTIONS.some((d) => {
        const o = at.get(`${q.x + dx(d)},${q.y + dy(d)}`);
        return o !== undefined && Math.abs(o - k) > 1;
      }),
    );
    if (touches) continue;
    for (const q of path) L.set(q.x, q.y, 'hose');
    return;
  }
}

/**
 * The tidiest way for a hose from its tap (heading `inDir`) to `goal`: fewest bends first, then
 * shortest (a search over tile and heading, a bend costing three steps), through tiles `open`.
 */
function hoseRoute(
  L: Layout,
  tap: { x: number; y: number },
  inDir: Direction,
  goal: { x: number; y: number },
  open: (x: number, y: number) => boolean,
): { x: number; y: number }[] | null {
  const { w } = L;
  const key = (x: number, y: number, d: number) => (y * w + x) * 4 + d;
  const cost = new Map<number, number>();
  const prev = new Map<number, number>();
  const start = key(tap.x, tap.y, inDir);
  cost.set(start, 0);
  const queue: number[] = [start];
  const done = new Set<number>();
  while (queue.length > 0) {
    // (Small grids: picking the cheapest by scan is plenty.)
    let best = 0;
    for (let k = 1; k < queue.length; k++)
      if (cost.get(queue[k]!)! < cost.get(queue[best]!)!) best = k;
    const cur = queue.splice(best, 1)[0]!;
    if (done.has(cur)) continue;
    done.add(cur);
    const d = cur % 4;
    const cell = Math.floor(cur / 4);
    const x = cell % w;
    const y = Math.floor(cell / w);
    if (x === goal.x && y === goal.y) {
      const out: { x: number; y: number }[] = [];
      for (let k: number | undefined = cur; k !== undefined; k = prev.get(k)) {
        const c = Math.floor(k / 4);
        out.unshift({ x: c % w, y: Math.floor(c / w) });
      }
      return out;
    }
    // Straight on first out of the tap; after that, straight or a quarter turn.
    const turns = cur === start ? [d] : [d, (d + 1) % 4, (d + 3) % 4];
    for (const nd of turns) {
      const nx = x + dx(nd as Direction);
      const ny = y + dy(nd as Direction);
      if (!open(nx, ny)) continue;
      const nk = key(nx, ny, nd);
      const c = cost.get(cur)! + 1 + (nd === d ? 0 : 3);
      if (c < (cost.get(nk) ?? Infinity)) {
        cost.set(nk, c);
        prev.set(nk, cur);
        queue.push(nk);
      }
    }
  }
  return null;
}

/**
 * A dog house in the upper half of the lawn with its door (the tile below) on open grass; the
 * dog's home is the doorway. Returns the doorway, or null if there is no room.
 */
function placeDogHouse(
  L: Layout,
  rng: Rng,
  protectedTile: (x: number, y: number) => boolean,
  topRows: number,
): { x: number; y: number } | null {
  const grass = (x: number, y: number) => L.get(x, y) === 'grass' && !protectedTile(x, y);
  const spots = L.cells().filter(
    ({ x, y }) =>
      y >= topRows &&
      y <= Math.floor(L.h / 2) &&
      grass(x, y) &&
      grass(x, y + 1) &&
      // Room around the doorway for the dog to come and go.
      [-1, 1].every((ox) => L.open(x + ox, y + 1)) &&
      L.open(x, y + 2),
  );
  if (spots.length === 0) return null;
  const spot = rng.pick(spots);
  L.set(spot.x, spot.y, 'dogHouse');
  return { x: spot.x, y: spot.y + 1 };
}

/**
 * A sprinkler head on open lawn: all 8 tiles around it plain grass (its spray soaks only lawn the
 * mower can drive), off the start runway and clear of other sprinklers' reach.
 */
function placeSprinkler(
  L: Layout,
  rng: Rng,
  protectedTile: (x: number, y: number) => boolean,
  /** How far from the fence the head stands (its slippery spray never fills a corner lane). */
  margin = 1,
  /** A pair, mirrored left to right (formal gardens). */
  mirrored = false,
): boolean {
  const around = (x: number, y: number, ok: (ax: number, ay: number) => boolean) => {
    for (let oy = -1; oy <= 1; oy++)
      for (let ox = -1; ox <= 1; ox++) if (!ok(x + ox, y + oy)) return false;
    return true;
  };
  const spots = L.cells().filter(
    ({ x, y }) =>
      x >= margin &&
      y >= margin &&
      x < L.w - margin &&
      y < L.h - 1 - margin &&
      around(x, y, (ax, ay) => L.get(ax, ay) === 'grass' && !protectedTile(ax, ay)) &&
      // Keep sprays apart.
      L.cells().every(
        (c) =>
          L.get(c.x, c.y) !== 'sprinkler' || Math.max(Math.abs(c.x - x), Math.abs(c.y - y)) > 3,
      ),
  );
  const usable = mirrored
    ? spots.filter(
        (s) =>
          Math.abs(L.w - 1 - 2 * s.x) > 6 &&
          spots.some((t) => t.x === L.w - 1 - s.x && t.y === s.y),
      )
    : spots;
  if (usable.length === 0) return false;
  const s = rng.pick(usable);
  L.set(s.x, s.y, 'sprinkler');
  if (mirrored) L.set(L.w - 1 - s.x, s.y, 'sprinkler');
  return true;
}

/**
 * Lets the lawn grow long: all plain grass (outside the start runway) becomes tall grass, then
 * round patches of ordinary grass are carved back until about `1 - share` of it is short.
 */
function overgrow(
  L: Layout,
  share: number,
  rng: Rng,
  protectedTile: (x: number, y: number) => boolean,
): void {
  const grass = L.cells().filter(({ x, y }) => L.get(x, y) === 'grass' && !protectedTile(x, y));
  for (const { x, y } of grass) L.set(x, y, 'tallGrass');
  let short = Math.round(grass.length * (1 - share));
  for (let tries = 0; tries < 200 && short > 0; tries++) {
    const c = rng.pick(grass);
    const r = rng.intBetween(1, 2);
    for (let oy = -r; oy <= r; oy++) {
      for (let ox = -r; ox <= r; ox++) {
        if (ox * ox + oy * oy > r * r + 1 || short <= 0) continue;
        if (L.get(c.x + ox, c.y + oy) !== 'tallGrass') continue;
        L.set(c.x + ox, c.y + oy, 'grass');
        short--;
      }
    }
  }
}
